import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "item", // each positionable row/item
    "handle", // drag handle inside an item
    "positionData", // position data inside an item
  ];

  connect() {
    this.dragged = null;

    this.initItems();
    this.updatePositions();

    this.observer = new MutationObserver(() => {
      this.initItems();
      this.updatePositions();
    });
    this.observer.observe(this.element, {
      childList: false,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  initItems() {
    this.items.forEach((item) => {
      // Ensure the entire item is NOT draggable
      item.removeAttribute("draggable");
      item.removeEventListener("dragstart", this.onDragStart);
      item.removeEventListener("dragend", this.onDragEnd);

      const handle = item.querySelector(this.targetSelector("handle"));
      if (!handle) return;
      if (handle.dataset.positionableInitialized === "true") return;

      handle.setAttribute("draggable", "true");
      handle.addEventListener("dragstart", this.onDragStart);
      handle.addEventListener("dragend", this.onDragEnd);
      handle.dataset.positionableInitialized = "true";
    });

    if (!this.element.dataset.positionableContainerInitialized) {
      this.element.addEventListener("dragover", this.onDragOver);
      this.element.dataset.positionableContainerInitialized = "true";
    }
  }

  get items() {
    return this.itemTargets || [];
  }

  onDragStart = (e) => {
    const handle = e.currentTarget;
    const item = handle.closest(this.targetSelector("item"));
    if (!item) return;
    this.dragged = item;
    // changes to DOM must be handled asynchronously; Chromium-based browsers
    // will immediately fire the `dragend` event and break the drag if a DOM
    // modification occurs within a `dragstart` callback for some reason
    setTimeout(() => this.dragged.classList.add("dragging"), 0.01);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  onDragEnd = (e) => {
    const dragged = this.dragged;
    if (this.dragged) {
      this.dragged.classList.remove("dragging");
      this.dragged = null;
    }
    this.updatePositions();
    if (dragged) {
      this.recordUpdatedPosition(dragged);
    }
  };

  onDragOver = (e) => {
    if (!this.dragged) return;
    e.preventDefault();
    const targetElement = this.getTargetElement(e.clientX, e.clientY);
    if (targetElement) {
      if (this.dragged.compareDocumentPosition(targetElement) == Node.DOCUMENT_POSITION_FOLLOWING) {
        if (!targetElement.nextElementSibling) {
          this.element.appendChild(this.dragged);
        } else {
          this.element.insertBefore(this.dragged, targetElement.nextElementSibling);
        }
      } else {
        this.element.insertBefore(this.dragged, targetElement);
      }
    }
  };

  getTargetElement(x, y) {
    const candidates = this.items.filter((el) => el !== this.dragged);
    let target = null;
    candidates.some((child) => {
      const box = child.getBoundingClientRect();
      if (x > box.left && x < box.right && y > box.top && y < box.bottom) {
        target = child;
        return true;
      }
    });
    return target;
  }

  updatePositions() {
    let index = this.getLowestPositionValue();
    this.items.forEach((item) => {
      const positionData = item.querySelector(this.targetSelector("positionData"));
      if (positionData) {
        positionData.dataset.positionIndex = index++;
      }
    });
  }

  getLowestPositionValue() {
    let lowest = Number.POSITIVE_INFINITY;
    this.items.forEach((item) => {
      const positionData = item.querySelector(this.targetSelector("positionData"));
      if (positionData && positionData.dataset.positionIndex < lowest) {
        lowest = positionData.dataset.positionIndex;
      }
    });
    return lowest === Number.POSITIVE_INFINITY ? 1 : lowest;
  }

  recordUpdatedPosition(item) {
    const positionData = item.querySelector(this.targetSelector("positionData"));
    const positionableId = positionData.dataset.positionableId;
    const scopableId = positionData.dataset.scopableId;
    const positionIndex = positionData.dataset.positionIndex;
    fetch("/positions", {
      method: "PUT",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        position: {
          nu_positionable_id: positionableId,
          nu_scopable_id: scopableId,
          destination: positionIndex,
        },
      }),
    });
  }

  // Build a selector for a Stimulus target for this controller, e.g.
  // [data-forms--positionable-target="item"]
  targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`;
  }
}
